import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { of, Observable, forkJoin, EMPTY } from 'rxjs';
import { map, switchMap, catchError, withLatestFrom, mergeMap } from 'rxjs/operators';
import { DatasetApiService } from './dataset-api.service';
import * as datasetActions from './dataset.actions';
import * as datasetSelectors from './dataset.selectors';
import * as fromRouting from '../../../core/store/routing';
import { GUID_EMPTY } from '@app/core/constants';
import { QueryParams } from 'src/app/modules/core/store/models/query.model';
import { TranslateService } from '@ngx-translate/core';
import { FileApiService } from '../files/file-api.service';
import { FileVersion } from '../files/file.model';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

@Injectable()
export class DatasetEffects {
  constructor(
    private actions$: Actions,
    private datasetApiService: DatasetApiService,
    private store$: Store<any>,
    private notify: SystemNotificationsService,
    private fileApiService: FileApiService,
    private translateService: TranslateService
  ) {}

  saveDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.saveDataset),
      switchMap(action => {
        const datasetToSave = action.dataset;
        if (datasetToSave.uId === GUID_EMPTY) {
          return [datasetActions.createDataset({ dataset: datasetToSave })];
        }

        return [datasetActions.updateDataset({ dataset: datasetToSave })];
      })
    )
  );

  createDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.createDataset),
      switchMap(action => {
        const datasetToCreate = action.dataset;

        return this.datasetApiService.createDataset(datasetToCreate).pipe(
          map(dataset => datasetActions.createDatasetComplete({ dataset })),
          catchError(err => of(datasetActions.setError({ err })))
        );
      })
    )
  );

  createDatasetComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.createDatasetComplete),
      switchMap(action => {
        return [
          datasetActions.deleteDataset({ uId: GUID_EMPTY }),
          datasetActions.addDataset({ dataset: action.dataset }),
          datasetActions.setSelectedDataset({ uId: action.dataset.uId }),
          datasetActions.hideDatasetProperties()
        ];
      })
    )
  );

  deleteSelectedDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.deleteSelectedDataset),
      withLatestFrom(
        this.store$.select(datasetSelectors.selectDatasetForDelete),
        this.store$.select(datasetSelectors.selectQueryParams),
        this.store$.select(fromRouting.selectCurrentUrl)
      ),
      switchMap(([action, datasetToDelete, queryParams, url]) => {
        return this.datasetApiService.deleteDataset(datasetToDelete).pipe(
          switchMap(res => this.datasetApiService.getDatasets(queryParams)),
          map(res => {
            const actions: Observable<Action>[] = [];

            if (url.includes(`/datasets/${datasetToDelete.uId}`)) {
              return fromRouting.back();
            }

            if (res.skip >= res.totalCount) {
              const newParams = {
                ...queryParams,
                skip: queryParams.skip - queryParams.take
              };
              return datasetActions.getDatasets({ params: newParams });
            } else {
              return datasetActions.loadDatasets({ results: res });
            }
          }),
          catchError(err => of(datasetActions.setError({ err })))
        );
      })
    )
  );

  getDatasets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.getDatasets),
      switchMap(action => {
        const { searchTerm } = action.params;

        return this.datasetApiService.getDatasets(action.params, !!action.includeWorkflow).pipe(
          map(res => datasetActions.loadDatasets({ results: { ...res, searchTerm } })),
          catchError(err => of(datasetActions.setError({ err })))
        );
      })
    )
  );

  loadDatasetsForSelect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.loadDatasetsForSelect),
      switchMap(action => {
        const queryParams: QueryParams = {
          skip: 0,
          take: 500,
          sortField: 'Name',
          sortDirection: 'Ascending'
        };

        return this.datasetApiService.getDatasets(queryParams, true, true).pipe(
          map(res => datasetActions.loadDatasetsForSelectComplete({ results: res })),
          catchError(err => of(datasetActions.setError({ err })))
        );
      })
    )
  );

  setSelectedDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.setSelectedDataset),
      switchMap(action => {
        let nextActions = [];

        nextActions.push(
          datasetActions.setSelectedDatasetComplete({
            uId: action.uId
          })
        );

        if (!action.skipNavigate) {
          nextActions.push(
            fromRouting.go({
              routeData: { path: ['management', 'datasets', action.uId] }
            })
          );
        }

        return nextActions;
      })
    )
  );

  updateDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.updateDataset),
      withLatestFrom(this.store$.select(fromRouting.selectCurrentUrl)),
      switchMap(([action, url]) => {
        const dataset = action.dataset;

        return this.datasetApiService.updateDataset(dataset).pipe(
          switchMap(res => {
            return [
              datasetActions.loadDatasetSchemaWithTranslations(),
              datasetActions.updateDatasetComplete({
                dataset: {
                  id: dataset.uId,
                  changes: {
                    ...dataset
                  }
                }
              })
            ];
          }),
          catchError(err => of(datasetActions.setError({ err })))
        );
      })
    )
  );

  cancelEditDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.cancelEditDataset),

      switchMap(action => {
        return [datasetActions.cancelEditDatasetComplete(), fromRouting.back()];
      })
    )
  );

  getDatasetShareSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.getDatasetShareSettingsFromApi),
      withLatestFrom(this.store$.select(datasetSelectors.selectSelectedDatasetUID)),
      switchMap(([action, datasetUId]) => {
        return this.datasetApiService.getDatasetShareSettings(datasetUId, action.params).pipe(
          map(results => datasetActions.getDatasetShareSettingsFromApiComplete({ results })),
          catchError(err => of(datasetActions.setError({ err })))
        );
      })
    )
  );

  loadDatasetSchemaWithTranslations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.loadDatasetSchemaWithTranslations),
      withLatestFrom(this.store$.select(datasetSelectors.selectSelectedDatasetUID)),
      switchMap(([action, datasetUId]) => {
        return this.datasetApiService.getDatasetSchemaWithTranslations(datasetUId).pipe(
          map(results => datasetActions.loadDatasetSchemaWithTranslationsComplete({ schema: results })),
          catchError(err => of(datasetActions.setError({ err })))
        );
      })
    )
  );

  shareDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.shareDataset),
      switchMap(action => {
        return this.datasetApiService.shareDataset(action.organizationUId, action.datasetUId).pipe(
          map(results => datasetActions.shareDatasetComplete()),
          catchError(err => of(datasetActions.setError({ err })))
        );
      })
    )
  );

  shareDatasetComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.shareDatasetComplete),
      withLatestFrom(this.store$.select(datasetSelectors.selectShareSettingsQueryParams)),
      switchMap(([action, params]) => {
        return [datasetActions.getDatasetShareSettingsFromApi({ params })];
      })
    )
  );

  unshareDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.unshareDataset),
      switchMap(action => {
        return this.datasetApiService.unshareDataset(action.organizationUId, action.datasetUId).pipe(
          map(results => datasetActions.unshareDatasetComplete()),
          catchError(err => of(datasetActions.setError({ err })))
        );
      })
    )
  );

  unshareDatasetComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.unshareDatasetComplete),
      withLatestFrom(this.store$.select(datasetSelectors.selectShareSettingsQueryParams)),
      switchMap(([action, params]) => {
        return [datasetActions.getDatasetShareSettingsFromApi({ params })];
      })
    )
  );

  getAvailableSubscriptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.getAvailableSubscriptionsFromApi),
      switchMap(action => {
        return this.datasetApiService.getAvailableDatasetSubscriptions(action.params).pipe(
          map(results => datasetActions.getAvailableSubscriptionsFromApiComplete({ results })),
          catchError(err => of(datasetActions.setError({ err })))
        );
      })
    )
  );

  subscribeToDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.subscribeToDataset),
      withLatestFrom(this.store$.select(datasetSelectors.selectQueryParams)),
      switchMap(([action, queryParams]) => {
        return this.datasetApiService.subscribeToDataset(action.datasetUId).pipe(
          map(results => datasetActions.getDatasets({ params: queryParams })),
          catchError(err => of(datasetActions.setError({ err })))
        );
      })
    )
  );

  loadDatasetExportTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.loadDatasetExportTemplates),
      withLatestFrom(this.store$.select(datasetSelectors.selectSelectedDatasetUID)),
      switchMap(([action, datasetUId]) => {
        if (datasetUId && datasetUId !== GUID_EMPTY) {
          return this.datasetApiService.getDatasetExportTemplates(datasetUId, action.params).pipe(
            map(results => datasetActions.loadDatasetExportTemplatesComplete({ results })),
            catchError(err => of(datasetActions.setError({ err })))
          );
        } else {
          return EMPTY;
        }
      })
    )
  );

  createExportTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.createExportTemplate),
      withLatestFrom(this.store$.select(datasetSelectors.selectSelectedDatasetUID)),
      switchMap(([action, datasetUId]) => {
        return this.datasetApiService.createExportTemplate(action.template).pipe(
          map(result => datasetActions.createExportTemplateComplete({ template: result })),
          catchError(err => of(datasetActions.setError({ err })))
        );
      })
    )
  );

  updateExportTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.updateExportTemplate),
      withLatestFrom(this.store$.select(datasetSelectors.selectSelectedDatasetUID)),
      switchMap(([action, datasetUId]) => {
        return this.datasetApiService.updateExportTemplate(action.template).pipe(
          map(result => datasetActions.updateExportTemplateComplete({ template: result })),
          catchError(err => of(datasetActions.setError({ err })))
        );
      })
    )
  );

  deleteExportTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(datasetActions.deleteExportTemplate),
      withLatestFrom(this.store$.select(datasetSelectors.selectSelectedDatasetUID)),
      switchMap(([action, datasetUId]) => {
        return this.datasetApiService.deleteExportTemplate(action.template).pipe(
          mergeMap(result => {
            const forkActions = [];

            const fileVersion = {
              filePath: action.template.uId,
              fileName: action.template.fileName
            } as FileVersion;

            forkActions.push(
              this.fileApiService
                .deleteFile(action.template.datasetUId, fileVersion)
                .pipe(catchError(err => of(datasetActions.setError({ err }))))
            );

            return forkJoin(forkActions).pipe(map(res => datasetActions.deleteExportTemplateComplete({ template: action.template })));
          }),
          catchError(err => of(datasetActions.setError({ err })))
        );
      })
    )
  );

  setError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(datasetActions.setError),
        switchMap(action => {
          const message = action.err?.message;

          if (Boolean(message)) {
            const errorMessage = this.translateService.instant(`ERRORS.${message}`);
            this.notify.error(errorMessage);
            return [];
          } else {
            console.log(action.err);
            return [];
          }
        })
      ),
    {
      dispatch: false
    }
  );
}
